/**
 * The platform shell (design-system/MASTER.md, owner decision 2026-11-02: everything white). A white grouped rail
 * carries the module list; one slim white top bar carries the page context, the clock with its zone and a small
 * connection chip. The EBR is one module among several, which is also how the system is actually built.
 *
 * Nothing here is dark. The offline band is rendered only when the device is actually offline or entries are waiting.
 */
import { formatInZone } from "@corpex-ebr/domain/signature/binding";
import { useQuery } from "@tanstack/react-query";
import { lazy, type ReactNode, Suspense, useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import logo from "../assets/corpex-logo.png";
import { aiApi } from "../lib/ai-api.js";
import { endpoints, SESSION_ENDED, setToken } from "../lib/api.js";
import { control } from "../lib/control-api.js";
import { unackSummary } from "../lib/control-model.js";
import { CLASS_CHANGED, setWorkstationClass, workstationClass } from "../lib/idle-lock.js";
import { alarms } from "../lib/records-api.js";
import "../styles/stitch/shell.css";
import { ConnectionChip, OfflineBanner } from "./OfflineBanner.jsx";

/** URS-CTL-021 / ISA-18.2: the persistent alarm banner of the control screens, loaded only when one is open. */
const ControlAlarmBanner = lazy(async () => ({
  default: (await import("./ControlAlarmBanner.jsx")).ControlAlarmBanner,
}));
/** Station strip (GAP C10): lazy, fixed height, so the entry bundle stays lean and nothing moves. */
const ControlStationBar = lazy(async () => ({
  default: (await import("./ControlStationBar.jsx")).ControlStationBar,
}));
const STATION_SLOT = <div aria-hidden="true" className="h-9 shrink-0 border-b border-line bg-surface" />;
/** Height reserved while it loads, equal to the banner's own, so the page below never moves. */
const BANNER_SLOT = <div aria-hidden="true" className="h-11 shrink-0 border-b border-line bg-surface" />;

import { cx, Icon, Tabs as KitTabs } from "./ui/index.js";

/** The sub-groups of Process Control in the rail: what you do with the plant, how you configure it, how you rehearse it. */
export type RailGroupName = "Operate" | "Engineer" | "Simulate";

export interface NavItem {
  label: string;
  icon: string;
  href?: string;
  /** A module with sub-items is a section of the rail. */
  children?: { label: string; href: string; badge?: number; group?: RailGroupName }[];
  badge?: number;
}

/**
 * The module list.
 *
 * Every entry here leads to a screen that exists and is backed by an endpoint. Modules the platform will grow
 * — logbooks, a consumption log, notifications — are tracked in the
 * requirements ledger (BL-019), not advertised in the rail: a navigation item that lands you back where you started is
 * indistinguishable from a fault, and CLAUDE.md forbids shipping a control that does nothing.
 */
export const NAV: NavItem[] = [
  { label: "Home", icon: "home", href: "/" },
  {
    label: "Electronic Batch Records",
    icon: "description",
    children: [
      { label: "Batch runs", href: "/" },
      { label: "Master batch records", href: "/mbr" },
      { label: "Process parameters", href: "/reference/limits" },
      { label: "Batch dashboard", href: "/dashboard" },
      { label: "Production board", href: "/board" },
      { label: "Campaigns", href: "/campaigns" },
      { label: "Site transfers", href: "/site-transfers" },
      { label: "Dispensing worklist", href: "/dispensing" },
      { label: "Dispense labels", href: "/dispensing/labels" },
    ],
  },
  {
    label: "Process Control",
    icon: "tune",
    children: [
      { label: "Setup", href: "/control/setup", group: "Engineer" },
      { label: "Graphics editor", href: "/control/setup/graphics", group: "Engineer" },
      { label: "Overview", href: "/control", group: "Operate" },
      { label: "Factory", href: "/control/factory", group: "Operate" },
      { label: "Enterprise view", href: "/control/enterprise", group: "Operate" },
      { label: "Plant alarms", href: "/control/alarms", group: "Operate" },
      { label: "Alarm management", href: "/control/alarms/manage", group: "Operate" },
      { label: "Shift log", href: "/control/shift-log", group: "Operate" },
      { label: "Trends", href: "/control/trends", group: "Operate" },
      { label: "Tags", href: "/control/tags", group: "Operate" },
      { label: "Event chronicle", href: "/control/events", group: "Operate" },
      { label: "Interlocks", href: "/control/interlocks", group: "Operate" },
      { label: "Devices", href: "/control/devices", group: "Operate" },
      { label: "Engineering", href: "/control/engineering/modules", group: "Engineer" },
      { label: "Plant model", href: "/control/engineering/plant", group: "Engineer" },
      { label: "Releases", href: "/control/engineering/releases", group: "Engineer" },
      { label: "Loop health", href: "/control/engineering/loop-health", group: "Engineer" },
      { label: "Loop check", href: "/control/engineering/loop-check", group: "Engineer" },
      { label: "Responsibility", href: "/control/engineering/scope", group: "Engineer" },
      { label: "Simulation", href: "/control/simulation", group: "Simulate" },
      { label: "Health", href: "/control/health", group: "Operate" },
    ],
  },
  { label: "Master data", icon: "database", href: "/master-data" },
  { label: "Offline queue", icon: "cloud_off", href: "/offline" },
  { label: "Audit trail", icon: "history", href: "/audit-trail" },
  { label: "Exceptions", icon: "rule", href: "/exceptions" },
  { label: "OOS investigations", icon: "science", href: "/oos" },
  { label: "Analytics", icon: "monitoring", href: "/analytics" },
  { label: "Reports", icon: "lab_profile", href: "/reports" },
];

const GROUP_ORDER: RailGroupName[] = ["Operate", "Engineer", "Simulate"];

/** Icons for the entries that sit inside a section. */
const CHILD_ICON: Record<string, string> = {
  "Batch runs": "assignment",
  "Master batch records": "fact_check",
  "Process parameters": "analytics",
  "Batch dashboard": "dashboard",
  "Production board": "view_kanban",
  Campaigns: "repeat",
  "Dispensing worklist": "scale",
  Setup: "settings",
  Overview: "dashboard",
  "Enterprise view": "domain",
  "Plant alarms": "notification_important",
  "Alarm management": "rule",
  "Shift log": "menu_book",
  Trends: "show_chart",
  Tags: "sell",
  Interlocks: "lock",
  Devices: "memory",
  Engineering: "engineering",
  "Plant model": "account_tree",
  Releases: "inventory_2",
  "Loop health": "vital_signs",
  Simulation: "science",
  Health: "monitor_heart",
};

const ITEM =
  "relative flex min-h-9 w-full items-center gap-3 rounded-md px-3 text-sm no-underline focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-2px] focus-visible:outline-brand";
const ITEM_ON = "bg-brand-subtle font-semibold text-brand";
const ITEM_OFF = "font-medium text-ink hover:bg-subtle";
const OVERLINE = "px-3 text-xs font-medium uppercase tracking-[0.04em] text-ink-muted";

function RailItem({
  label,
  icon,
  href,
  active,
  badge,
}: {
  label: string;
  icon: string;
  href: string;
  active: boolean;
  badge?: number | undefined;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cx(ITEM, active ? ITEM_ON : ITEM_OFF)}
    >
      <Icon name={icon} size={20} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge ? (
        <span className="rounded-full bg-alarm px-1.5 font-mono text-xs font-semibold leading-5 text-white">
          {badge}
        </span>
      ) : null}
    </Link>
  );
}

type Child = NonNullable<NavItem["children"]>[number];

/** A collapsible sub-group of a section. The group holding the current page is always open, so the rail never hides where you are. */
function RailGroup({ name, items, path }: { name: string; items: Child[]; path: string }) {
  const holdsCurrent = items.some((c) => c.href === path);
  const [open, setOpen] = useState(name === "Operate" || holdsCurrent);
  const shown = open || holdsCurrent;
  const count = items.reduce((n, c) => n + (c.badge ?? 0), 0);
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        aria-expanded={shown}
        onClick={() => setOpen(!shown)}
        className="flex min-h-8 w-full items-center gap-1 rounded-md bg-transparent px-3 text-left text-xs font-semibold text-ink-muted hover:bg-subtle"
      >
        <Icon name={shown ? "expand_more" : "chevron_right"} size={16} />
        <span className="flex-1">{name}</span>
        {!shown && count ? (
          <span className="rounded-full bg-alarm px-1.5 font-mono text-xs leading-5 text-white">{count}</span>
        ) : null}
      </button>
      {shown
        ? items.map((c) => (
            <RailItem
              key={c.label}
              label={c.label}
              icon={CHILD_ICON[c.label] ?? "circle"}
              href={c.href}
              active={c.href === path}
              badge={c.badge}
            />
          ))
        : null}
    </div>
  );
}

function RailSection({ item, path }: { item: NavItem; path: string }) {
  const children = item.children ?? [];
  const grouped = children.some((c) => c.group);
  return (
    <section aria-label={item.label} className="flex flex-col gap-1">
      <h2 className={cx(OVERLINE, "m-0 pb-1 pt-2")}>{item.label}</h2>
      {grouped
        ? GROUP_ORDER.map((g) => {
            const items = children.filter((c) => c.group === g);
            return items.length ? <RailGroup key={g} name={g} items={items} path={path} /> : null;
          })
        : children.map((c) => (
            <RailItem
              key={c.label}
              label={c.label}
              icon={CHILD_ICON[c.label] ?? item.icon}
              href={c.href}
              active={c.href === path}
              badge={c.badge}
            />
          ))}
    </section>
  );
}

/** Rail body: Home on its own, each module section, then the single entries under one "Records" heading. */
function RailBody({ modules, path }: { modules: NavItem[]; path: string }) {
  const single = (i: NavItem) => (
    <RailItem
      key={i.label}
      label={i.label}
      icon={i.icon}
      href={i.href ?? "/"}
      active={i.href === path}
      badge={i.badge}
    />
  );
  const home = modules[0] && !modules[0].children ? modules[0] : null;
  const rest = modules.filter((i) => i !== home);
  return (
    <div className="flex flex-col gap-3">
      {home ? single(home) : null}
      {rest
        .filter((i) => i.children)
        .map((i) => (
          <RailSection key={i.label} item={i} path={path} />
        ))}
      <section aria-label="Records and reports" className="flex flex-col gap-1">
        <h2 className={cx(OVERLINE, "m-0 pb-1 pt-2")}>Records</h2>
        {rest.filter((i) => !i.children).map(single)}
      </section>
    </div>
  );
}

/**
 * Which kind of workstation this device is (URS-11.7-007): a shop-floor device locks after the shorter interval,
 * an office one after the longer. The intervals are change-controlled policy (SEC-037); an unconfigured device is
 * a shop-floor device.
 */
function WorkstationClassToggle() {
  const [cls, setCls] = useState(() => workstationClass(localStorage));
  const flip = () => {
    const next = cls === "floor" ? "office" : "floor";
    setWorkstationClass(localStorage, next);
    setCls(next);
    window.dispatchEvent(new Event(CLASS_CHANGED));
  };
  return (
    <button
      type="button"
      onClick={flip}
      aria-label={`Workstation type: ${cls === "floor" ? "shop floor" : "office"}. Press to change it.`}
      className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-line-strong bg-surface px-1.5 text-xs font-medium text-ink hover:bg-subtle"
    >
      <Icon name={cls === "floor" ? "precision_manufacturing" : "desktop_windows"} size={16} />
      {cls === "floor" ? "Shop floor" : "Office"}
    </button>
  );
}

function initials(name: string | undefined): string {
  const parts = (name ?? "").split(/\s+/).filter(Boolean);
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return ((parts[0]?.[0] ?? "") + last).toUpperCase() || "?";
}

/**
 * Who is signed in, and the way out.
 *
 * Every act in this system is attributed to a person, so the screen has to say which person without being
 * asked. And a session that ends has to be leaveable: before this existed, an expired token left the operator
 * on a shell of refusals with no control to press.
 */
function RailIdentity() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me(), staleTime: 300_000, retry: false });
  return (
    <div className="flex flex-col gap-2 border-t border-line p-3">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-subtle font-mono text-xs font-semibold text-brand"
          aria-hidden="true"
        >
          {initials(me.data?.fullName)}
        </div>
        <div className="min-w-0">
          <p className="m-0 truncate text-sm font-semibold text-ink">{me.data?.fullName ?? "Signed in"}</p>
          <p className="m-0 truncate font-mono text-xs text-ink-muted">{me.data?.username ?? "…"}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <WorkstationClassToggle />
        <button
          type="button"
          onClick={() => {
            setToken(null);
            window.dispatchEvent(new Event(SESSION_ENDED));
          }}
          // the floor surface is used in gloves: nothing tappable may be smaller than this (§10.2)
          className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-line-strong bg-surface px-1.5 text-xs font-medium text-ink hover:bg-subtle"
        >
          <Icon name="logout" size={16} />
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * The header's clock text: the instant in the site zone with the zone label always shown (TIME-007), the same
 * rule signature manifestations and the audit trail follow. Before the site zone is known, UTC.
 */
export function clockText(iso: string, siteTz: string | undefined): string {
  return formatInZone(iso, siteTz ?? "UTC");
}

/** The header's clock: the browser's own instant. The record's time is the server's (N5); this is a convenience. */
function SiteClock({ tz }: { tz: string | undefined }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <time dateTime={now.toISOString()} className="font-mono text-xs font-medium tabular-nums text-ink">
      {clockText(now.toISOString(), tz)}
    </time>
  );
}

type Linked = { href?: string | undefined };

/** The rail entry whose page this is, as "Module / Page". */
function exactContext(modules: NavItem[], path: string): string[] | null {
  for (const m of modules) {
    if (m.href === path && m.children === undefined) return [m.label];
    const c = m.children?.find((k) => k.href === path);
    if (c) return [m.label, c.label];
  }
  return null;
}

/** For a page below a rail entry (/master-data/materials, /mbr/:id/designer): the closest entry above it. */
function closestContext(modules: NavItem[], path: string): string[] | null {
  const under = (href: string | undefined) =>
    href !== undefined && href !== "/" && path.startsWith(`${href}/`);
  const best = <T extends Linked>(items: T[]): T | undefined =>
    items.filter((i) => under(i.href)).sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0];
  for (const m of modules) {
    const c = m.children ? best(m.children) : undefined;
    if (c) return [m.label, c.label];
  }
  const top = best(modules);
  return top ? [top.label] : null;
}

/** The top bar's page context: "Module / Page", from the rail entry that matches the path. */
export function contextOf(modules: NavItem[], path: string): string[] {
  if (path.startsWith("/batches/")) return ["Electronic Batch Records", "Batch record"];
  return (
    exactContext(modules, path) ??
    closestContext(modules, path) ??
    (path.startsWith("/control") ? ["Process Control"] : [])
  );
}

/** How often the unacknowledged-alarm count is refreshed while the shell is open. */
const ALARM_POLL_MS = 60_000;

/**
 * ERR-016 / BL-025: roles that may see alarms get an "Alarms" entry whose badge counts the alarms nobody has
 * acknowledged; the icon changes when any of them is CRITICAL. Roles without alarm.view see nothing at all.
 */
export function useAlarmNavItem(): NavItem | null {
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me(), staleTime: 300_000, retry: false });
  const canSee = me.data?.permissions.includes("alarm.view") ?? false;
  const unack = useQuery({
    queryKey: ["alarms-unacknowledged"],
    queryFn: () => alarms.unacknowledged(),
    enabled: canSee,
    refetchInterval: ALARM_POLL_MS,
    retry: false,
  });
  if (!canSee) return null;
  const critical = (unack.data?.critical ?? 0) > 0;
  return {
    label: "Alarms",
    icon: critical ? "notification_important" : "notifications",
    href: "/alarms",
    ...(unack.data?.total ? { badge: unack.data.total } : {}),
  };
}

/** How often the process-control alarm count in the rail is refreshed (the banner polls the same query key). */
const CONTROL_ALARM_POLL_MS = 5000;

/**
 * URS-CTL-021: the rail's process-control group carries the count of active unacknowledged plant alarms on its alarm
 * entry. No count when the control engine cannot be asked (the entry stays, the screens say why).
 */
function useControlRail(items: NavItem[]): NavItem[] {
  const q = useQuery({
    queryKey: ["control", "rail-alarms"],
    queryFn: () => control.alarms("active"),
    refetchInterval: CONTROL_ALARM_POLL_MS,
    retry: false,
  });
  const count = Array.isArray(q.data) ? unackSummary(q.data).count : 0;
  if (!count) return items;
  return items.map((i) =>
    i.label === "Process Control"
      ? {
          ...i,
          children: (i.children ?? []).map((c) => (c.label === "Plant alarms" ? { ...c, badge: count } : c)),
        }
      : i,
  );
}

/** The rail's modules, with the alarm entry placed beside the audit trail when the person may see alarms. */
/**
 * Draft-only AI assistance (EU GMP Annex 22 draft): the rail shows "AI drafts" only to people who may use it and only
 * when the site has configured a model. Unconfigured = no entry at all.
 */
function useAiNavItem(): NavItem | null {
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me(), staleTime: 300_000, retry: false });
  const can = me.data?.permissions.includes("ai.use") ?? false;
  const st = useQuery({
    queryKey: ["ai", "status"],
    queryFn: () => aiApi.status(),
    enabled: can,
    staleTime: 60_000,
    retry: false,
  });
  return can && st.data?.configured ? { label: "AI drafts", icon: "auto_awesome", href: "/ai" } : null;
}

function withAlarms(items: NavItem[], alarm: NavItem | null): NavItem[] {
  if (!alarm) return items;
  const at = items.findIndex((i) => i.label === "Audit trail");
  return at < 0 ? [...items, alarm] : [...items.slice(0, at + 1), alarm, ...items.slice(at + 1)];
}

function SkipLink() {
  return (
    // biome-ignore lint/a11y/useValidAnchor: a skip link; the handler moves focus into the page's main landmark
    <a
      href="#main"
      onClick={(e) => {
        // pages own their single <main>; move focus into it so the keyboard skips the rail
        const main = document.querySelector("main");
        if (!main) return;
        e.preventDefault();
        main.setAttribute("tabindex", "-1");
        main.focus();
      }}
      className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand focus:shadow-card"
    >
      Skip to content
    </a>
  );
}

function TopBar({ context }: { context: string[] }) {
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me(), staleTime: 300_000, retry: false });
  return (
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-6">
      <p
        className="m-0 flex min-w-0 items-center gap-1.5 truncate text-sm text-ink-muted"
        data-testid="page-context"
      >
        {context.map((c, i) => (
          <span key={c} className={i === context.length - 1 ? "font-medium text-ink" : undefined}>
            {i > 0 ? <span aria-hidden="true"> / </span> : null}
            {c}
          </span>
        ))}
      </p>
      <div className="flex shrink-0 items-center gap-4">
        <ConnectionChip />
        <SiteClock tz={me.data?.siteTz} />
      </div>
    </header>
  );
}

export function AppShell({
  children,
  path,
  location = path,
}: {
  children: ReactNode;
  /** The rail entry to highlight (a batch page highlights its list). */
  path: string;
  /** Where the person really is, for the top bar's page context. */
  location?: string;
}) {
  const alarmItem = useAlarmNavItem();
  const aiItem = useAiNavItem();
  const modules = useControlRail(withAlarms(withAlarms(NAV, alarmItem), aiItem));
  return (
    <div className="flex min-h-screen bg-page font-body text-ink antialiased has-[[data-fit-viewport]]:h-screen">
      <SkipLink />
      {/* The rail column stretches to the page height (white, with its edge) while the nav inside sticks to the viewport. */}
      <div className="w-[232px] shrink-0 border-r border-line bg-surface">
        <nav aria-label="Modules" className="sticky top-0 flex h-screen select-none flex-col">
          <div className="flex flex-col gap-1 border-b border-line px-4 py-3">
            <img src={logo} alt="Corpex" width={112} height={29} className="block h-auto w-28" />
            <p className="m-0 text-xs text-ink-muted">Electronic batch records</p>
          </div>
          <div className="quiet-scroll min-h-0 flex-1 overflow-y-auto px-3 py-2">
            <RailBody modules={modules} path={path} />
          </div>
          <RailIdentity />
        </nav>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar context={contextOf(modules, location)} />
        <OfflineBanner />
        {location.startsWith("/control") ? (
          <Suspense fallback={BANNER_SLOT}>
            <ControlAlarmBanner />
          </Suspense>
        ) : null}
        {location.startsWith("/control") ? (
          <Suspense fallback={STATION_SLOT}>
            <ControlStationBar />
          </Suspense>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/** A record page header: back, identity, and the one action this record affords. */
export function RecordHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="flex items-center gap-4 border-b border-line bg-surface px-6 pb-3 pt-4">
      <Link
        href="/"
        aria-label="Back to batch runs"
        className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line-strong text-ink-muted no-underline hover:bg-subtle"
      >
        <Icon name="arrow_back" size={20} />
      </Link>
      <div className="min-w-0 flex-1">
        <h1 className="m-0 font-display text-2xl font-semibold text-ink">{title}</h1>
        {subtitle ? <p className="m-0 mt-0.5 text-xs text-ink-muted">{subtitle}</p> : null}
      </div>
      {action}
    </header>
  );
}

/** The section tabs of a record (link tabs; the kit's Tabs does the work). */
export function Tabs({
  items,
  current,
}: {
  items: { label: string; icon: string; href: string }[];
  current: string;
}) {
  return (
    <div className="border-b border-line bg-surface px-6">
      <KitTabs
        bare
        label="Record sections"
        value={current}
        items={items.map((t) => ({ id: t.href, label: t.label, icon: t.icon, href: t.href }))}
      />
    </div>
  );
}

/** Used by routes to know which rail item is current without prop-drilling the router. */
export function useCurrentPath(): string {
  const [isBatch, params] = useRoute("/batches/:id/:tab?");
  return isBatch ? `/batches/${params?.id}` : window.location.pathname;
}
